import { z } from 'zod';
import { useMemo } from 'react';
import {
    CommanderFormProps,
    SchemaFormFields,
    useDialogForm,
    useHandledDialogFormControls,
} from '@mews-commander/form';
import { createZodFormResolver, lt, showSuccessAlert, getDefaults } from '@mews/framework';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { Body, Stack } from '@optimus-web/core';
import { useCalendarService } from '@mews-commander/ui';
import { createSchema, FormValues } from './schema';
import { createFormDefinition } from './formDefinition';

export const useUpdateGoLiveDate = () => {
    return useDialogForm({
        id: 'update-go-live-date',
        cancelLabel: lt('Close'),
        saveLabel: lt('Submit'),
        title: lt('OnboardingAuditGoLiveDateDialogTitle'),
        renderForm: (props) => <UpdateGoLiveDate {...props} />,
    });
};

const UpdateGoLiveDate = (props: CommanderFormProps) => {
    const calendarService = useCalendarService();
    const schema = useMemo(() => createSchema(calendarService), [calendarService]);
    const defaultValues = getDefaults(schema) as z.infer<typeof schema>;

    const { mutateAsync } = useRepositoryMutation('enterprises', 'updateGoLiveDate', {
        invalidateQueries: {
            enterprises: ['getOnboardingDetails'],
        },
        onSuccess: () => {
            showSuccessAlert({
                title: lt('OnboardingAuditGoLiveDateDialogSuccessMessage'),
            });
        },
    });

    const { renderFormContent } = useHandledDialogFormControls<FormValues>({
        values: defaultValues,
        onSubmit: async (values) => mutateAsync(values),
        resolver: createZodFormResolver(schema),
        ...props,
    });

    return renderFormContent(
        <Stack spacing="250" vertical>
            <Body size="small" variant="secondary">
                {lt('OnboardingAuditGoLiveDateDialogDescription')}
            </Body>
            <SchemaFormFields schema={schema} formDefinition={createFormDefinition(calendarService)} />
        </Stack>
    );
};
